import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Shield, Clock, CheckCircle, AlertTriangle,
  ArrowRight, Zap, Globe, Lock, Server, Package, Star,
  Monitor, ShoppingCart, BarChart3, X, Loader2, Send,
  Phone, Mail, User, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── PACKAGE REQUEST MODAL ───
const PackageRequestModal = ({ isOpen, onClose, selectedPkg, t }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", package_name: selectedPkg || "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        interest: `Paquete e-CF: ${selectedPkg || form.package_name}`,
        source: "ecf_package",
      });
      setSent(true);
    } catch {
      toast.error("Error al enviar solicitud");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-[#0a0f1a] border border-white/10 p-6 sm:p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="ecf-package-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-[#94a3b8] hover:text-white">
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="text-center py-6">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-white text-xl font-bold mb-2">{t("ecf_form_success_title")}</h3>
            <p className="text-[#94a3b8] text-sm">{t("ecf_form_success_msg")}</p>
            <button onClick={onClose} className="mt-6 bg-[#f59e0b] text-black font-bold px-6 py-2.5 text-sm hover:bg-[#d97706]">
              {t("ecf_form_close")}
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-white text-lg font-bold mb-1">{t("ecf_form_title")}</h3>
            <p className="text-[#94a3b8] text-sm mb-5">
              {selectedPkg && <span className="text-[#f59e0b] font-semibold">{selectedPkg}</span>}
              {selectedPkg && " — "}{t("ecf_form_subtitle")}
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder={t("ecf_form_name")} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="ecf-form-name" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder={t("ecf_form_email")} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="ecf-form-email" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("ecf_form_phone")} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="ecf-form-phone" />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("ecf_form_company")} className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="ecf-form-company" />
              </div>
              <motion.button
                type="submit"
                disabled={sending}
                className="w-full bg-[#f59e0b] text-black font-bold py-3 text-sm flex items-center justify-center gap-2 hover:bg-[#d97706] disabled:opacity-50"
                whileHover={{ boxShadow: "0 0 25px rgba(245,158,11,0.3)" }}
                data-testid="ecf-form-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t("ecf_form_send")}
              </motion.button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

const EcfSection = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("packages");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState("");

  const deadlines = [
    { type: t("ecf_deadline_big"), date: t("ecf_deadline_big_date"), urgent: true },
    { type: t("ecf_deadline_medium"), date: "15 nov 2025", urgent: true },
    { type: t("ecf_deadline_small"), date: "15 may 2026", urgent: false },
  ];

  const benefits = [
    { icon: Shield, title: t("ecf_benefit_1_title"), desc: t("ecf_benefit_1_desc") },
    { icon: Zap, title: t("ecf_benefit_2_title"), desc: t("ecf_benefit_2_desc") },
    { icon: Globe, title: t("ecf_benefit_3_title"), desc: t("ecf_benefit_3_desc") },
    { icon: Lock, title: t("ecf_benefit_4_title"), desc: t("ecf_benefit_4_desc") },
  ];

  const steps = [
    { num: 1, title: t("ecf_step_1_title"), desc: t("ecf_step_1_desc") },
    { num: 2, title: t("ecf_step_2_title"), desc: t("ecf_step_2_desc") },
    { num: 3, title: t("ecf_step_3_title"), desc: t("ecf_step_3_desc") },
    { num: 4, title: t("ecf_step_4_title"), desc: t("ecf_step_4_desc") },
  ];

  const packages = [
    {
      name: t("ecf_pkg_basic_name"),
      price: "$150",
      period: "USD",
      desc: t("ecf_pkg_basic_desc"),
      features: [
        t("ecf_pkg_basic_f1"),
        t("ecf_pkg_basic_f2"),
        t("ecf_pkg_basic_f3"),
        t("ecf_pkg_basic_f4"),
      ],
      featured: false,
    },
    {
      name: t("ecf_pkg_complete_name"),
      price: "$300",
      period: "USD",
      desc: t("ecf_pkg_complete_desc"),
      features: [
        t("ecf_pkg_complete_f1"),
        t("ecf_pkg_complete_f2"),
        t("ecf_pkg_complete_f3"),
        t("ecf_pkg_complete_f4"),
        t("ecf_pkg_complete_f5"),
        t("ecf_pkg_complete_f6"),
      ],
      featured: true,
      badge: t("ecf_pkg_popular"),
    },
    {
      name: t("ecf_pkg_premium_name"),
      price: "$500",
      period: "USD",
      desc: t("ecf_pkg_premium_desc"),
      features: [
        t("ecf_pkg_premium_f1"),
        t("ecf_pkg_premium_f2"),
        t("ecf_pkg_premium_f3"),
        t("ecf_pkg_premium_f4"),
        t("ecf_pkg_premium_f5"),
        t("ecf_pkg_premium_f6"),
        t("ecf_pkg_premium_f7"),
      ],
      featured: false,
    },
  ];

  const compatibleSystems = [
    { icon: BarChart3, name: t("ecf_sys_accounting"), desc: t("ecf_sys_accounting_desc") },
    { icon: ShoppingCart, name: t("ecf_sys_pos"), desc: t("ecf_sys_pos_desc") },
    { icon: Package, name: t("ecf_sys_inventory"), desc: t("ecf_sys_inventory_desc") },
  ];

  const openModal = (pkgName) => {
    setSelectedPkg(pkgName);
    setModalOpen(true);
  };

  return (
    <section id="facturacion" className="relative py-24 sm:py-32 overflow-hidden" data-testid="ecf-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#02040a] via-[#0a1628] to-[#02040a]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#f59e0b]/3 rounded-full blur-[200px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#f59e0b]/10 border border-[#f59e0b]/20 text-[#f59e0b] text-xs font-semibold tracking-widest uppercase mb-6">
            <FileText className="w-3.5 h-3.5" />
            {t("ecf_badge")}
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            {t("ecf_title_1")} <span className="text-[#f59e0b]">{t("ecf_title_2")}</span>
          </h2>
          <p className="text-[#94a3b8] max-w-2xl mx-auto text-base sm:text-lg">
            {t("ecf_subtitle")}
          </p>
        </motion.div>

        {/* Alert Banner */}
        <motion.div
          className="mb-12 p-4 sm:p-6 bg-[#f59e0b]/5 border border-[#f59e0b]/20 rounded-sm flex items-start gap-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <AlertTriangle className="w-6 h-6 text-[#f59e0b] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-semibold mb-1">{t("ecf_alert_title")}</p>
            <p className="text-[#94a3b8] text-sm">{t("ecf_alert_desc")}</p>
          </div>
        </motion.div>

        {/* Compatible Systems */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-center text-[#94a3b8] text-xs font-semibold tracking-widest uppercase mb-5">
            {t("ecf_compatible_with")}
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {compatibleSystems.map((sys, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 p-4 bg-[#0a0f1a] border border-white/5 hover:border-emerald-500/20 transition-colors group"
              >
                <div className="w-12 h-12 bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                  <sys.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-bold text-sm">{sys.name}</p>
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-[#94a3b8] text-xs">{sys.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          {[
            { id: "packages", label: t("ecf_tab_packages") },
            { id: "why", label: t("ecf_tab_why") },
            { id: "deadlines", label: t("ecf_tab_deadlines") },
            { id: "how", label: t("ecf_tab_how") },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`ecf-tab-${tab.id}`}
              className={`px-5 py-2.5 text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? "bg-[#f59e0b] text-black"
                  : "bg-white/5 text-[#94a3b8] hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "packages" && (
            <motion.div
              key="packages"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                {packages.map((pkg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12 }}
                    className={`relative bg-[#0a0f1a] border p-6 flex flex-col transition-colors ${
                      pkg.featured
                        ? "border-[#f59e0b]/40 shadow-[0_0_40px_rgba(245,158,11,0.08)]"
                        : "border-white/5 hover:border-[#f59e0b]/20"
                    }`}
                    data-testid={`ecf-package-${i}`}
                  >
                    {pkg.badge && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f59e0b] text-black text-[10px] font-black px-4 py-1 uppercase tracking-wider flex items-center gap-1">
                        <Star className="w-3 h-3" /> {pkg.badge}
                      </span>
                    )}
                    <div className="mb-5">
                      <h3 className="text-white font-bold text-lg">{pkg.name}</h3>
                      <p className="text-[#94a3b8] text-xs mt-1">{pkg.desc}</p>
                    </div>
                    <div className="mb-5">
                      <span className="text-4xl font-black text-white">{pkg.price}</span>
                      <span className="text-[#94a3b8] text-sm ml-1">{pkg.period}</span>
                    </div>
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {pkg.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-[#94a3b8]">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.button
                      onClick={() => openModal(pkg.name)}
                      className={`w-full py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                        pkg.featured
                          ? "bg-[#f59e0b] text-black hover:bg-[#d97706]"
                          : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                      }`}
                      whileHover={{ boxShadow: pkg.featured ? "0 0 25px rgba(245,158,11,0.3)" : "none" }}
                      data-testid={`ecf-pkg-btn-${i}`}
                    >
                      {t("ecf_pkg_cta")} <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </motion.div>
                ))}
              </div>

              {/* Monthly service note */}
              <div className="text-center">
                <p className="text-[#94a3b8] text-sm">
                  {t("ecf_pkg_monthly_note")} <span className="text-[#f59e0b] font-semibold">{t("ecf_pkg_monthly_price")}</span>
                </p>
              </div>
            </motion.div>
          )}

          {activeTab === "why" && (
            <motion.div
              key="why"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
            >
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#0a0f1a] border border-white/5 p-6 hover:border-[#f59e0b]/20 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#f59e0b]/10 flex items-center justify-center mb-4 group-hover:bg-[#f59e0b]/20 transition-colors">
                    <b.icon className="w-6 h-6 text-[#f59e0b]" />
                  </div>
                  <h3 className="text-white font-bold mb-2">{b.title}</h3>
                  <p className="text-[#94a3b8] text-sm leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "deadlines" && (
            <motion.div
              key="deadlines"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="max-w-2xl mx-auto space-y-4"
            >
              {deadlines.map((d, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`flex items-center justify-between p-5 border ${
                    d.urgent ? "bg-red-500/5 border-red-500/20" : "bg-[#0a0f1a] border-white/5"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Clock className={`w-5 h-5 ${d.urgent ? "text-red-400" : "text-[#f59e0b]"}`} />
                    <span className="text-white font-medium text-sm sm:text-base">{d.type}</span>
                  </div>
                  <span className={`text-sm font-bold px-3 py-1 ${
                    d.urgent ? "bg-red-500/10 text-red-400" : "bg-[#f59e0b]/10 text-[#f59e0b]"
                  }`}>
                    {d.date}
                  </span>
                </motion.div>
              ))}
              <p className="text-[#94a3b8] text-sm text-center mt-6 italic">
                {t("ecf_deadline_note")}
              </p>
            </motion.div>
          )}

          {activeTab === "how" && (
            <motion.div
              key="how"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="max-w-3xl mx-auto"
            >
              <div className="space-y-6">
                {steps.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.12 }}
                    className="flex gap-5 group"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 bg-[#f59e0b] text-black font-black flex items-center justify-center text-lg flex-shrink-0">
                        {step.num}
                      </div>
                      {i < steps.length - 1 && <div className="w-px h-full bg-[#f59e0b]/20 mt-2" />}
                    </div>
                    <div className="pb-8">
                      <h4 className="text-white font-bold text-lg mb-1">{step.title}</h4>
                      <p className="text-[#94a3b8] text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => openModal("")}
              data-testid="ecf-cta-contact"
              className="group inline-flex items-center gap-2 bg-[#f59e0b] text-black font-bold px-8 py-3.5 text-sm hover:bg-[#d97706] transition-colors"
            >
              {t("ecf_cta_contact")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="/login"
              data-testid="ecf-cta-portal"
              className="inline-flex items-center gap-2 border border-[#f59e0b]/30 text-[#f59e0b] font-bold px-8 py-3.5 text-sm hover:bg-[#f59e0b]/5 transition-colors"
            >
              <Server className="w-4 h-4" />
              {t("ecf_cta_portal")}
            </a>
          </div>
          <p className="text-[#94a3b8] text-xs mt-4">{t("ecf_cta_note")}</p>
        </motion.div>
      </div>

      <PackageRequestModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedPkg={selectedPkg}
        t={t}
      />
    </section>
  );
};

export default EcfSection;
